import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  getCertificationList,
  getCertificationById,
  createCertification,
  updateCertification,
  patchCertification,
  deleteCertification,
} from "../api/certifications.api";
import type {
  CertificationPayload,
  GetCertificationListParams,
} from "@/interface/profile";
import { certificationsKeys, candidateProfileKeys, STALE_5_MIN } from "@/lib/queryKey";

export const useCertificationList = (params?: GetCertificationListParams) =>
  useQuery({
    queryKey: certificationsKeys.list(params),
    queryFn: () => getCertificationList(params),
    ...STALE_5_MIN,
  });

export const useCertificationDetail = (id: number | string, enabled = true) =>
  useQuery({
    queryKey: certificationsKeys.detail(id),
    queryFn: () => getCertificationById(id),
    enabled: Boolean(id) && enabled,
    ...STALE_5_MIN,
  });

export const useCreateCertification = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: createCertification,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: certificationsKeys.lists() });
      queryClient.invalidateQueries({ queryKey: candidateProfileKeys.certifications() });
      queryClient.invalidateQueries({ queryKey: ["profile-completion"] });
    },
  });
};

export const useUpdateCertification = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      id,
      payload,
    }: {
      id: number | string;
      payload: CertificationPayload;
    }) => updateCertification(id, payload),
    onSuccess: (_, { id }) => {
      queryClient.invalidateQueries({ queryKey: certificationsKeys.lists() });
      queryClient.invalidateQueries({
        queryKey: certificationsKeys.detail(id),
      });
      queryClient.invalidateQueries({ queryKey: candidateProfileKeys.certifications() });
      queryClient.invalidateQueries({ queryKey: ["profile-completion"] });
    },
  });
};

export const usePatchCertification = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      id,
      payload,
    }: {
      id: number | string;
      payload: Partial<CertificationPayload>;
    }) => patchCertification(id, payload),
    onSuccess: (_, { id }) => {
      queryClient.invalidateQueries({ queryKey: certificationsKeys.lists() });
      queryClient.invalidateQueries({
        queryKey: certificationsKeys.detail(id),
      });
      queryClient.invalidateQueries({ queryKey: candidateProfileKeys.certifications() });
      queryClient.invalidateQueries({ queryKey: ["profile-completion"] });
    },
  });
};

export const useDeleteCertification = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: deleteCertification,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: certificationsKeys.lists() });
      queryClient.invalidateQueries({ queryKey: candidateProfileKeys.certifications() });
      queryClient.invalidateQueries({ queryKey: ["profile-completion"] });
    },
  });
};
